import type { ChangeEvent, ReactNode } from 'react';
import React, { useRef } from 'react';

import _ from 'lodash';

import { ImageUploaderIcon } from 'src/app/components/icons/image-upload-icon';
import { LinkButton } from 'src/app/components/lib/button/link-button';
import { FileUploaderPrompt } from 'src/app/components/lib/file-uploader';
import { FormControl } from 'src/app/components/lib/form-control';
import { ImageCard } from 'src/app/components/lib/image-card';
import { Label } from 'src/app/components/lib/label';

import { ErrorMessage } from './error-message';
import { Container, Grid, HiddenInput } from './image-input.styles';

import type { SxProps } from '@mui/material';

export type ImageInputProps = {
  accept?: string;
  className?: string;
  disabled?: boolean;
  errorMessage?: string;
  fullWidth?: boolean;
  images?: string[];
  label?: ReactNode | string;
  labelPlacement?: 'image-uploader' | 'top';
  limit?: number;
  maintainAspectRatio?: boolean;
  onImageDelete: (imageIndex: number) => void;
  onImageSelect: (event: ChangeEvent<HTMLInputElement>) => void;
  promptButtonLabel?: ReactNode;
  promptHeader?: ReactNode;
  promptMessage?: ReactNode;
  required?: boolean;
  sx?: SxProps;
};

export function ImageInput(props: ImageInputProps) {
  const {
    accept = 'image/*',
    className,
    disabled = false,
    fullWidth = false,
    images = [],
    label,
    labelPlacement = 'image-uploader',
    limit,
    maintainAspectRatio = false,
    promptButtonLabel = 'Choose file',
    promptHeader = <ImageUploaderIcon />,
    promptMessage = 'Please upload image files only (.png, .jpg, .gif and so on)',
    required = false,
    onImageDelete,
    onImageSelect,
    sx,
    errorMessage,
  } = props;

  const hiddenInputRef = useRef(null);

  // computed
  const isEmpty = !images.length;
  const isFull = images.length >= limit;
  const gridColumnCount = maintainAspectRatio ? 1 : 3;

  // methods
  function resetHiddenInputValue() {
    hiddenInputRef.current.value = null;
  }

  function simulateHiddenInputClick() {
    hiddenInputRef.current.click();
  }

  // handlers
  function handlePromptClick() {
    simulateHiddenInputClick();
  }

  function handleHiddenInputChange(event: ChangeEvent<HTMLInputElement>) {
    onImageSelect(event);
    resetHiddenInputValue();
  }

  return (
    <FormControl
      $labelPlacement={labelPlacement}
      className={className}
      id={`form-control-image-group_${typeof label === 'string' ? label : ''}`}
      sx={sx}
    >
      {label && <Label required={required}>{label}</Label>}

      <Container $fullWidth={fullWidth}>
        {/* empty state */}
        {isEmpty && (
          <FileUploaderPrompt
            buttonLabel={promptButtonLabel}
            disabled={disabled}
            header={promptHeader}
            message={promptMessage}
            onClick={handlePromptClick}
          />
        )}

        {/* populated state */}
        {!isEmpty && (
          <>
            <Grid $gridColumnCount={gridColumnCount}>
              {images.map((image, index) => (
                <ImageCard
                  automationId={`image-input-${index}`}
                  disabled={disabled}
                  isThumbnail={!maintainAspectRatio}
                  key={image}
                  src={image}
                  onButtonClick={() => (disabled ? _.noop : onImageDelete(index))}
                />
              ))}
            </Grid>
            {!isFull && (
              <LinkButton disabled={disabled} label='+ Add image' sx={{ mt: '16px' }} onClick={handlePromptClick} />
            )}
          </>
        )}
      </Container>
      {errorMessage && (
        <ErrorMessage $fullWidth={!label} error>
          {errorMessage}
        </ErrorMessage>
      )}

      <HiddenInput
        accept={accept}
        data-testid='hidden-input'
        disabled={disabled}
        ref={hiddenInputRef}
        type='file'
        onChange={handleHiddenInputChange}
      />
    </FormControl>
  );
}
